import * as THREE from 'three'
import { useLayoutEffect, useMemo, useRef, useState, useEffect } from 'react'
import { Canvas, extend, useFrame, useLoader } from '@react-three/fiber'
import { Image, ScrollControls, useScroll, Billboard, Text, OrbitControls } from '@react-three/drei'
import { suspend } from 'suspend-react'
import { generate } from 'random-words'
import { easing, geometry } from 'maath'
import { TextureLoader } from 'three'
import React, { useContext } from 'react';
import { LanguageContext } from './context/LanguageContext';

extend(geometry)
const inter = import('@pmndrs/assets/fonts/inter_regular.woff')

// Adicione este objeto no início do arquivo, após os imports
const categoryImages = {
  prints: [
    { url: '/images/prints/print1.jpg', title: 'cabeça feita', year: '2024' },
    // ... adicione mais obras
  ],
  textiles: [
    { url: '/images/textiles/textile1.jpg', title: 'sem nome', year: '2010' },
    // { url: '/images/textiles/textile3.jpg', title: 'Obra Textile 1', year: '2021' },
    // ... adicione mais obras
  ],
  'digital art': [
    { url: '/images/digital/digital1.jpg', title: 'metasonho', year: '2020' },
    { url: '/images/digital/digital2.jpg', title: 'fragmento de memória', year: '2021' },
    // { url: '/images/digital/digital3.jpg', title: 'Obra Digital 3', year: '2023' },
    // ... adicione mais obras
  ],
  'film/video': [
    { url: '/images/film/film1.jpg', title: 'solvitur ambulando', year: '2022' },
    // { url: '/images/film/film2.jpg', title: 'Obra Film 2', year: '2020' },
    // ... adicione mais obras
  ],
  photography: [
    { url: '/images/photo/photo1.jpg', title: 'simetria', year: '2013' },
    { url: '/images/photo/photo2.jpg', title: 'sem titulo', year: '2012' },
    // ... adicione mais obras
  ],
  performance: [
    { url: '/images/performance/perf1.jpeg', title: 'holograma', year: '2019' },
    // ... adicione mais obras
  ],
  installation: [
    { url: '/images/installation/inst1.jpg', title: 'retratos do cotidiano', year: '2023' },
    { url: '/images/installation/inst2.jpg', title: 'binário', year: '2018' },
    { url: '/images/installation/inst3.jpg', title: 'sem nome', year: '2010' },
    // ... adicione mais obras
  ],
  sculpture: [
    { url: '/images/sculpture/sculpt1.jpg', title: 'brasil suspenso', year: '2022' },
    // ... adicione mais obras
  ]
};

const categoryTranslations = {
  prints: {
    EN: "prints",
    PT: "gravura"
  },
  textiles: {
    EN: "textiles",
    PT: "têxteis"
  },
  "digital art": {
    EN: "digital art",
    PT: "arte digital"
  },
  "film/video": {
    EN: "film/video",
    PT: "filme/vídeo"
  },
  photography: {
    EN: "photography",
    PT: "fotografia"
  },
  performance: {
    EN: "performance",
    PT: "performance"
  },
  installation: {
    EN: "installation",
    PT: "instalação"
  },
  sculpture: {
    EN: "sculpture / objects",
    PT: "escultura / objetos"
  }
};

function App() {
  return (
    <Canvas dpr={[1, 1.5]}>
      <ScrollControls pages={4} infinite>
        <Scene position={[0, 1.5, 0]} />
      </ScrollControls>
    </Canvas>
  )
}

function Scene({ children, ...props }) {
  const ref = useRef();
  const scroll = useScroll();
  const [hovered, hover] = useState(null);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [isScrolling, setIsScrolling] = useState(false);
  // Utilize scroll?.offset com fallback para 0 se scroll for null
  const lastScrollOffset = useRef(scroll?.offset || 0);
  const lastCameraPosition = useRef([0, 0, 9]);
  const currentCameraPosition = useRef([0, 0, 9]);

  useEffect(() => {
    if (!scroll) return; // aguarda scroll estar definido
    const checkScrolling = () => {
      // utilize scroll?.offset com fallback para 0
      const currentOffset = scroll?.offset || 0;
      if (lastScrollOffset.current !== currentOffset) {
        setIsScrolling(true);
        // Use a posição atual da câmera armazenada
        lastCameraPosition.current = [...currentCameraPosition.current];
        clearTimeout(window.scrollTimeout);
        window.scrollTimeout = setTimeout(() => setIsScrolling(false), 150);
      }
      lastScrollOffset.current = currentOffset;
    };

    window.addEventListener('wheel', checkScrolling);
    return () => {
      window.removeEventListener('wheel', checkScrolling);
      clearTimeout(window.scrollTimeout);
    };
  }, [scroll]);

  useFrame((state, delta) => {
    // utilize scroll?.offset com fallback para 0
    const scrollOffset = scroll?.offset || 0;
    ref.current.rotation.y = -scrollOffset * (Math.PI * 2);
    state.events.update();

    // Armazena a posição atual da câmera
    currentCameraPosition.current = [
      -state.pointer.x * 2,
      state.pointer.y * 2 + 4.5,
      9
    ];

    if (isScrolling) {
      // câmera travada na última posição conhecida durante o scroll
      state.camera.position.set(
        lastCameraPosition.current[0],
        lastCameraPosition.current[1],
        lastCameraPosition.current[2]
      );
    } else {
      // movimento normal da câmera baseado no ponteiro
      easing.damp3(
        state.camera.position,
        [-state.pointer.x * 2, state.pointer.y * 2 + 4.5, 9],
        0.3,
        delta
      );
    }
    state.camera.lookAt(0, 0, 0);
  });

  const handlePointerOver = (category) => (index) => {
    hover(index);
    setHoveredCategory(category);
  };

  const handlePointerOut = () => {
    hover(null);
    setHoveredCategory(null);
  };

  return (
    <group ref={ref} {...props}>
      <Cards category="prints" from={0} len={Math.PI / 4.5}
        onPointerOver={handlePointerOver("prints")}
        onPointerOut={handlePointerOut} />
      <Cards category="textiles" from={Math.PI / 4.5} len={Math.PI / 4.5} position={[0, 0.4, 0]}
        onPointerOver={handlePointerOver("textiles")}
        onPointerOut={handlePointerOut} />
      <Cards category="digital art" from={Math.PI / 2.25} len={Math.PI / 4.5}
        onPointerOver={handlePointerOver("digital art")}
        onPointerOut={handlePointerOut} />
      <Cards category="film/video" from={Math.PI * 0.75} len={Math.PI / 4.5} position={[0, -0.4, 0]}
        onPointerOver={handlePointerOver("film/video")}
        onPointerOut={handlePointerOut} />
      <Cards category="photography" from={Math.PI} len={Math.PI / 4.5} position={[0, 0.2, 0]}
        onPointerOver={handlePointerOver("photography")}
        onPointerOut={handlePointerOut} />
      <Cards category="performance" from={Math.PI * 1.25} len={Math.PI / 4.5} position={[0, -0.2, 0]}
        onPointerOver={handlePointerOver("performance")}
        onPointerOut={handlePointerOut} />
      <Cards category="installation" from={Math.PI * 1.5} len={Math.PI / 4.5} position={[0, 0, 0]}
        onPointerOver={handlePointerOver("installation")}
        onPointerOut={handlePointerOut} />
      <Cards category="sculpture" from={Math.PI * 1.75} len={Math.PI / 4.5} position={[0, 0.3, 0]}
        onPointerOver={handlePointerOver("sculpture")}
        onPointerOut={handlePointerOut} />
      <ActiveCard hovered={hovered} category={hoveredCategory} />
    </group>
  );
}

function Cards({ category, from = 0, len = Math.PI * 2, radius = 5.25, onPointerOver, onPointerOut, ...props }) {
  const { language } = useContext(LanguageContext);
  const [hovered, hover] = useState(null);
  const amount = Math.round(len * 22);
  const textPosition = from + (amount / 2 / amount) * len;
  const images = categoryImages[category] || [];
  
  return (
    <group {...props}>
      <Billboard position={[Math.sin(textPosition) * radius * 1.4, 0.5, Math.cos(textPosition) * radius * 1.4]}>
        <Text font={suspend(inter).default} fontSize={0.25} anchorX="center" color="black">
          {categoryTranslations[category][language]}
        </Text>
      </Billboard>
      {Array.from({ length: amount - 3 }, (_, i) => {
        const angle = from + (i / amount) * len;
        const imageIndex = i % images.length;
        return (
          <Card
            key={angle}
            onPointerOver={(e) => (e.stopPropagation(), hover(i), onPointerOver(i))}
            onPointerOut={() => (hover(null), onPointerOut(null))}
            position={[Math.sin(angle) * radius, 0, Math.cos(angle) * radius]}
            rotation={[0, Math.PI / 2 + angle, 0]}
            active={hovered !== null}
            hovered={hovered === i}
            url={images[imageIndex].url}
          />
        )
      })}
    </group>
  )
}

function Card({ url, active, hovered, ...props }) {
  const ref = useRef()
  useFrame((state, delta) => {
    const f = hovered ? 1.4 : active ? 1.25 : 1
    easing.damp3(ref.current.position, [0, hovered ? 0.25 : 0, 0], 0.1, delta)
    easing.damp3(ref.current.scale, [1.618 * f, 1 * f, 1], 0.15, delta)
  })
  return (
    <group {...props}>
      <Image ref={ref} transparent radius={0.075} url={url} scale={[1.618, 1, 1]} side={THREE.DoubleSide} />
    </group>
  )
}

function ActiveCard({ hovered, category, ...props }) {
  const ref = useRef();

  useLayoutEffect(() => {
    if (ref.current) {
      ref.current.material.zoom = 0.8;
    }
  }, [hovered]);

  useFrame((state, delta) => {
    if (ref.current) {
      easing.damp(ref.current.material, 'zoom', 1, 0.5, delta);
      easing.damp(ref.current.material, 'opacity', hovered !== null ? 1 : 0, 0.3, delta);
    }
  });

  // Obtenha os dados do cartão (obra) da categoria atual
  const artworks = categoryImages[category] || [];
  const artwork =
    hovered !== null && artworks.length > 0
      ? artworks[hovered % artworks.length]
      : null;

  // Carrega a textura para determinar a orientação da imagem
  // Usamos uma imagem placeholder caso artwork seja null (não será renderizada)
  const texture = useLoader(
    TextureLoader,
    artwork ? artwork.url : '/images/placeholder.jpg'
  );
  const isLandscape =
    artwork && texture.image && texture.image.width > texture.image.height;
  // Defina a escala: se for landscape, aumenta a largura
  const imageScale = isLandscape
    ? [3.5 * 1.5, 1.618 * 3.5, 0.2]
    : [3.5, 1.618 * 3.5, 0.2];

  return (
    <Billboard {...props}>
      {artwork && (
        <>
          <Text
            font={suspend(inter).default}
            fontSize={0.3}
            position={[2.15, 3.85, 0]}
            anchorX="left"
            color="black"
          >
            {artwork.title} ({artwork.year})
          </Text>
          <Image
            ref={ref}
            transparent
            radius={0.3}
            position={[0, 1.5, 0]}
            scale={imageScale}
            url={artwork.url}
          />
        </>
      )}
    </Billboard>
  );
}

export default App
